import React, { useState, useMemo, useCallback } from 'react';
import EmailResultsModal from './EmailResultsModal';
import PrintPreview from './PrintPreview';
import Tooltip from './Tooltip';
import { useTenant } from '../TenantContext';
import { Attribute } from '../../types';
import { AID_CALCULATION_OPTIONS, ENTITY_NAMES } from '../../constants';
import { toTabKey } from '../../utils/toTabKey';

const formatAsCurrency = (numValue: number): string =>
    new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(numValue);

const formatCurrency = (
    value: string | number | null | undefined,
    fieldName?: string,
    hasOptedOut?: boolean,
    attributeTypeId?: string,
    estimatedAdditionalFundsOperandIds?: Set<string>
): string => {
    // Check if this is Estimated Additional Funds or its operands when user has opted out
    if (hasOptedOut && (fieldName === 'Estimated Additional Funds' || (attributeTypeId && estimatedAdditionalFundsOperandIds?.has(attributeTypeId)))) {
        return 'N/A';
    }

    // Show $0 for Need-Based Grant Estimate when user has opted out
    if (hasOptedOut && fieldName === 'Need-Based Grant Estimate') {
        return formatAsCurrency(0);
    }

    if (value === null || value === undefined || value === '') return 'N/A';

    const numValue = typeof value === 'string'
        ? Number.parseFloat(value.replace(/[^0-9.-]/g, ''))
        : value;

    if (Number.isNaN(numValue)) return 'N/A';

    // Show N/A for Scout Award when value is $0
    if (fieldName === 'Scout Award' && numValue === 0) {
        return 'N/A';
    }

    // Show N/A for Parent PLUS Loan when value is $0
    if (fieldName === 'Parent PLUS Loan' && numValue === 0) {
        return 'N/A';
    }

    return formatAsCurrency(numValue);
};

const formatDate = (value: string | number | null | undefined): string => {
    if (value === null || value === undefined) return 'N/A';

    try {
        const date = new Date(value);
        if (isNaN(date.getTime())) return 'N/A';

        return new Intl.DateTimeFormat('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        }).format(date);
    } catch {
        return 'N/A';
    }
};

const ensureCurrencyFormat = (value: string): string => {
    if (value === 'N/A') return value;
    if (value.startsWith('$')) return value;

    const numValue = Number(value.replace(/[^0-9.-]/g, ''));
    if (isNaN(numValue)) return value;

    return formatAsCurrency(numValue);
};

const shouldHideValue = (value: string): boolean => {
    return value === 'N/A' || value === '$0' || value === 'null' || value === null || value === undefined || value === '';
};

interface EstimatedPriceProps {
    derivedAttributesData?: { objects: { attributes: Attribute[] }[] } | null;
    estimateCalculated: boolean;
    isSummary?: boolean;
    currentTab?: string;
    disclaimerText?: string;
    noteText?: string;
}

interface Section {
    _id: string;
    label: string;
    price: string;
    type: 'expandable' | 'static';
    bold: boolean;
    order: number;
    color?: string;
    dataType?: string;
    toolTip?: string;
    description?: string;
    subSections?: SubSection[];
    isEstimatedAdditionalFundsOperand?: boolean;
}

interface SubSection {
    label: string;
    price: string;
    details: Detail[];
    order: number;
}

interface Detail {
    item: string;
    cost: string;
}

const SummaryActionButton: React.FC<{ label: string; color?: string; onClick: () => void }> = ({ label, color, onClick }) => (
    <button
        type="button"
        className="w-full bg-transparent font-bold py-3 px-6 rounded-lg transition-colors duration-300"
        style={{ color, border: `2px solid ${color}` }}
        onClick={onClick}
    >
        {label}
    </button>
);

const EstimatedPrice: React.FC<EstimatedPriceProps> = ({
    derivedAttributesData,
    estimateCalculated,
    isSummary = false,
    currentTab,
    disclaimerText,
    noteText,
}) => {
    const { tenantConfig, loading, getAttributesByEntityName, estimateId } = useTenant();
    const [openSection, setOpenSection] = useState<string | null>(null);

    // Check if the current page is after Academic Scores (excludes Academic Scores itself)
    const isAfterAcademicScores = useMemo(() => {
        if (!currentTab) return false;

        const afterAcademicScoresPages = [
            toTabKey(ENTITY_NAMES.FAMILY_INFORMATION),
            toTabKey(ENTITY_NAMES.PARENT_FINANCES),
            toTabKey(ENTITY_NAMES.STUDENT_FINANCES),
            toTabKey(ENTITY_NAMES.SUMMARY),
        ];

        return afterAcademicScoresPages.includes(currentTab);
    }, [currentTab]);

    // Check if user opted out of need-based financial aid
    const hasOptedOut = useMemo(() => {
        const familyInfoObject = derivedAttributesData?.objects?.find((obj: any) => obj.name === 'FamilyInformation');
        const familyAttributes = familyInfoObject?.attributes || [];

        // Check by friendlyName first, then by name as fallback
        const aidPreferenceAttr = familyAttributes.find((attr: any) =>
            attr.friendlyName === 'student_aid_preference' ||
            (attr.name?.toLowerCase().includes('aid') && attr.name?.toLowerCase().includes('preference'))
        );

        if (!aidPreferenceAttr) return false;

        const selectedValue = aidPreferenceAttr.value ?? '';
        // Check for combined value OR individual values
        return selectedValue === AID_CALCULATION_OPTIONS.SKIP_INTERNATIONAL ||
            selectedValue === AID_CALCULATION_OPTIONS.SKIP_NO_NEED;
    }, [derivedAttributesData]);
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [showPrintPreview, setShowPrintPreview] = useState(false);

    const handleToggle = useCallback((_id: string) => {
        setOpenSection(prev => prev === _id ? null : _id);
    }, []);

    const data = useMemo(() => {
        if (loading) {
            return {
                header: { title: '', price: '' },
                sections: [],
            };
        }

        const initialEstimateAttributes = getAttributesByEntityName('Estimate');
        const allDerivedAttributes = derivedAttributesData?.objects?.flatMap(obj => obj.attributes) || [];

        const attributeMap = new Map<string, Attribute>();

        [...allDerivedAttributes, ...initialEstimateAttributes].forEach(attr => {
            if (attr.attributeTypeId) {
                attributeMap.set(attr.attributeTypeId, attr);
            }
        });

        // Helper to get attribute by name (for hardcoded sections)
        const getAttribute = (name: string): Attribute | undefined => {
            const derivedAttr = allDerivedAttributes.find((attr: any) => attr.name === name);
            if (derivedAttr) return derivedAttr;
            return initialEstimateAttributes.find((attr: any) => attr.name === name);
        };

        // Helper to get attribute value by name (for hardcoded sections)
        const getAttributeValueByName = (name: string): string | number => {
            if (!estimateCalculated) {
                return 'N/A';
            }
            const attribute = getAttribute(name);
            if (attribute?.value !== null && attribute?.value !== undefined && attribute?.value !== 'null' && !attribute?.isNa) {
                return attribute.value;
            }
            return attribute?.constantValue ?? attribute?.defaultValue ?? '0';
        };

        const getAttributeValue = (attr: Attribute | undefined): string | number => {
            if (!estimateCalculated || !attr) {
                return 'N/A';
            }

            if (attr.value !== null && attr.value !== undefined && attr.value !== 'null' && !attr.isNa) {
                return attr.value;
            }

            if (attr.constantValue !== null && attr.constantValue !== undefined) {
                return attr.constantValue;
            }

            if (attr.defaultValue !== null && attr.defaultValue !== undefined) {
                return attr.defaultValue;
            }

            return '0';
        };



        // Helper to get value with priority: operand.value > attribute value
        const getAttributeValueWithOperand = (attr: Attribute, operand?: any): string => {
            if (operand?.isNa) return 'N/A';
            if (operand?.value !== null && operand?.value !== undefined && operand?.value !== 'NULL' && operand?.value !== 'null') {
                return String(operand.value);
            }
            return String(getAttributeValue(attr));
        };

        // Recursive helper to get Details from grandchild operands
        const getChildDetails = (childAttr: Attribute, estimatedAdditionalFundsOperandIds: Set<string>): Detail[] => {
            if (!childAttr.operands || childAttr.operands.length === 0) {
                return [];
            }

            return [...childAttr.operands]
                .sort((firstOperand: any, secondOperand: any) => {
                    const firstAttr = attributeMap.get(firstOperand.attributeId);
                    const secondAttr = attributeMap.get(secondOperand.attributeId);
                    return (firstAttr?.order ?? 999) - (secondAttr?.order ?? 999);
                })
                .map((grandchildOperand: any) => {
                    const grandchildAttr = attributeMap.get(grandchildOperand.attributeId);
                    if (!grandchildAttr?.name) return null;

                    const value = getAttributeValueWithOperand(grandchildAttr, grandchildOperand);
                    const formattedCost = grandchildAttr.dataType === 'Date'
                        ? formatDate(value)
                        : formatCurrency(value, grandchildAttr.name, hasOptedOut, grandchildAttr.attributeTypeId, estimatedAdditionalFundsOperandIds);

                    // Hide individual detail items with $0 or N/A values (e.g., Virtual Session, Food, Loan Fees, Fees)
                    if (shouldHideValue(formattedCost)) {
                        return null;
                    }

                    return {
                        item: grandchildAttr.name,
                        cost: formattedCost
                    };
                })
                .filter((detail): detail is Detail => detail !== null);
        };

        // Recursive helper to get SubSections from child operands
        const getChildSections = (parentAttr: Attribute, estimatedAdditionalFundsOperandIds: Set<string>): SubSection[] => {
            if (!parentAttr.operands || parentAttr.operands.length === 0) {
                return [];
            }

            return parentAttr.operands
                .map((childOperand: any) => {
                    const childAttr = attributeMap.get(childOperand.attributeId);
                    if (!childAttr?.name) return null;

                    const value = getAttributeValueWithOperand(childAttr, childOperand);
                    const details = getChildDetails(childAttr, estimatedAdditionalFundsOperandIds);

                    return {
                        label: childAttr.name,
                        price: childAttr.dataType === 'Date'
                            ? formatDate(value)
                            : formatCurrency(value, childAttr.name, hasOptedOut, childAttr.attributeTypeId, estimatedAdditionalFundsOperandIds),
                        details,
                        order: childAttr.order
                    };
                })
                .filter((subsection): subsection is SubSection => subsection !== null)
                .sort((a, b) => a.order - b.order);
        };

        const buildSections = (): Section[] => {
            const sections: Section[] = [];
            const processedAttributes = new Set<string>();

            // Only hide Direct Costs and Indirect Costs (children of Cost of Attendance)
            // and their nested children (Tuition, Fees, Room & Board, etc.)
            const costOfAttendanceAttr = initialEstimateAttributes.find(attr => attr.name === 'Cost Of Attendance');
            const estimatedAdditionalFundsAttr = initialEstimateAttributes.find(attr => attr.name === 'Estimated Additional Funds');
            const childAttributeIds = new Set<string>();
            const estimatedAdditionalFundsOperandIds = new Set<string>();

            if (costOfAttendanceAttr?.operands) {
                costOfAttendanceAttr.operands.forEach((operand: any) => {
                    // Add Direct Costs and Indirect Costs
                    childAttributeIds.add(operand.attributeId);

                    // Add their nested children (Tuition, Fees, Room & Board, Books/Supplies, etc.)
                    const childAttr = attributeMap.get(operand.attributeId);
                    if (childAttr?.operands && childAttr.operands.length > 0) {
                        childAttr.operands.forEach((subOperand: any) => {
                            childAttributeIds.add(subOperand.attributeId);
                        });
                    }
                });
            }

            // Track Estimated Additional Funds operands dynamically from the API response
            if (estimatedAdditionalFundsAttr?.operands && estimatedAdditionalFundsAttr.operands.length > 0) {
                estimatedAdditionalFundsAttr.operands.forEach((operand: any) => {
                    if (operand.attributeId) {
                        estimatedAdditionalFundsOperandIds.add(operand.attributeId);
                    }
                });
            }

            // Build a set of all child attribute IDs (operands of all parent attributes)
            const allChildAttributeIds = new Set<string>();
            initialEstimateAttributes.forEach(attr => {
                if (attr.operands && attr.operands.length > 0) {
                    attr.operands.forEach((operand: any) => {
                        allChildAttributeIds.add(operand.attributeId);
                    });
                }
            });

            // Filter to get all estimate attributes except those nested under Cost of Attendance
            const estimateAttributes = initialEstimateAttributes.filter(attr =>
                attr.name &&
                attr.attributeTypeId &&
                !childAttributeIds.has(attr.attributeTypeId)
            );

            // Process top-level attributes, preserving original API order for stable sorting
            estimateAttributes.forEach((attr, index) => {
                if (!attr.attributeTypeId || processedAttributes.has(attr.attributeTypeId)) {
                    return;
                }

                processedAttributes.add(attr.attributeTypeId);

                const useColor = attr.name === 'Estimated Net Price';

                // Create hierarchical subsections for Cost of Attendance and Merit Scholarship Estimate
                const subSections = (attr.name === 'Cost Of Attendance' || attr.name === 'Merit Scholarship Estimate')
                    ? getChildSections(attr, estimatedAdditionalFundsOperandIds)
                    : [];

                // Format value based on data type
                let formattedPrice: string;
                if (attr.dataType === 'Date') {
                    formattedPrice = formatDate(getAttributeValue(attr));
                } else if (attr.dataType === 'String') {
                    const value = getAttributeValue(attr);
                    // For string attributes, use possibleValues if value is empty, otherwise show N/A
                    if (!value || value === '0' || value === 'N/A') {
                        formattedPrice = attr.possibleValues ?? 'N/A';
                    } else {
                        formattedPrice = String(value);
                    }
                } else {
                    // Show Merit Scholarship Estimate after Academic Scores page (starting from Family Information)
                    // Show Need-Based Grant Estimate, Total Scholarships & Grants, and Estimated Net Price only on Summary page
                    const shouldShowNAUntilSummary = !isSummary &&
                        (attr.name === 'Need-Based Grant Estimate' ||
                            attr.name === 'Total Scholarships & Grants' ||
                            attr.name === 'Estimated Net Price');

                    // Hide Merit Scholarship Estimate until user clicks Continue on Academic Scores page
                    const shouldHideMeritUntilAfterAcademicScores = !isAfterAcademicScores &&
                        attr.name === 'Merit Scholarship Estimate';

                    if (shouldShowNAUntilSummary || shouldHideMeritUntilAfterAcademicScores) {
                        formattedPrice = 'N/A';
                    } else {
                        formattedPrice = formatCurrency(
                            getAttributeValue(attr),
                            attr.name,
                            hasOptedOut,
                            attr.attributeTypeId,
                            estimatedAdditionalFundsOperandIds
                        );
                    }
                }

                // Dynamic bold logic based on API data structure
                // Key summary attributes should ALWAYS be bold
                // Other attributes: bold if isDerived AND not a child of another attribute
                const isEstimatedAdditionalFundsOperand = attr.attributeTypeId ? estimatedAdditionalFundsOperandIds.has(attr.attributeTypeId) : false;
                const isChildAttribute = attr.attributeTypeId ? allChildAttributeIds.has(attr.attributeTypeId) : false;
                const isDerivedAttribute = attr.isDerived === true;
                const isKeySummaryAttribute = ['Cost Of Attendance', 'Merit Scholarship Estimate', 'Need-Based Grant Estimate', 'Estimated Net Price', 'Estimated Additional Funds'].includes(attr.name ?? '');
                // Explicitly prevent operands of Estimated Additional Funds from being bold
                const shouldBeBold = isEstimatedAdditionalFundsOperand ? false : (isKeySummaryAttribute || (!isChildAttribute && isDerivedAttribute));

                sections.push({
                    _id: attr.name?.toLowerCase().replace(/\s+/g, '') ?? `attr-${attr.attributeTypeId}`,
                    label: attr.name ?? '',
                    description: attr.description ?? undefined,
                    price: formattedPrice,
                    type: subSections.length > 0 ? 'expandable' : 'static',
                    bold: shouldBeBold,
                    order: attr.order ?? 999,
                    dataType: attr.dataType,
                    color: useColor ? tenantConfig?.authority?.authorityBranding?.primaryColor : undefined,
                    toolTip: attr.toolTip ?? undefined,
                    subSections: subSections.length > 0 ? subSections : undefined,
                    isEstimatedAdditionalFundsOperand
                });
            });

            return sections.sort((a, b) => a.order - b.order);
        };

        const headerTitle = 'Your Estimated Net Price:';
        // Show N/A for header price until user reaches summary page
        const headerPrice = isSummary
            ? formatCurrency(getAttributeValueByName('Estimated Net Price'), 'Estimated Net Price', hasOptedOut)
            : 'N/A';

        return {
            header: {
                title: headerTitle,
                price: headerPrice
            },
            sections: buildSections(),
        };
    }, [loading, derivedAttributesData, getAttributesByEntityName, estimateCalculated, tenantConfig, hasOptedOut, isSummary, isAfterAcademicScores]);


    const backgroundColor = tenantConfig?.authority?.authorityBranding?.primaryColor;
    const textColor = tenantConfig?.authority?.authorityBranding?.accentColor;

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center bg-gray-50 pt-0 pb-4">
                <div className="w-full max-w-xl border rounded-lg overflow-hidden shadow-xl font-sans">
                    <output
                        className="text-white px-6 py-4 font-semibold text-lg text-center block"
                        style={{ backgroundColor }}
                        aria-live="polite"
                    >
                        Loading...
                    </output>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col items-center justify-center bg-gray-50 pt-0 pb-4">
            <div className="w-full max-w-xl border rounded-lg overflow-hidden shadow-xl font-sans">
                <header
                    className="text-white px-6 py-4 font-semibold text-lg"
                    style={{ backgroundColor }}
                >
                    <div className="flex items-center justify-between px-0">
                        <span className="text-base md:text-lg w-[70%]">{data.header.title}</span>
                        {!shouldHideValue(data.header.price) && (
                            <span
                                className="font-bold text-base md:text-lg tabular-nums w-[30%] text-left"
                                aria-label={`Price: ${data.header.price}`}
                            >
                                {data.header.price}
                            </span>
                        )}
                    </div>
                </header>

                <main className="pb-4">
                    {(() => {
                        const filteredSections = data.sections.filter(section => {
                            // Hide Estimated Additional Funds and its operands unless on Summary page
                            if (!isSummary && (section.label === 'Estimated Additional Funds' || section.isEstimatedAdditionalFundsOperand)) {
                                return false;
                            }

                            // On summary page, hide sections with N/A or $0 values
                            // Exception: Merit Scholarship Estimate and Need-Based Grant Estimate always show
                            // (label only, without the value, when their value is zero/null/undefined)
                            if (isSummary) {
                                const isEstimateDisplayAlways =
                                    section.label === 'Merit Scholarship Estimate' ||
                                    section.label === 'Need-Based Grant Estimate';
                                if (isEstimateDisplayAlways) return true;
                                return !shouldHideValue(section.price);
                            }
                            return true;
                        });

                        // Find the index of the last Estimated Additional Funds operand in the filtered array
                        const lastOperandIndex = filteredSections.reduce((lastIdx, s, i) =>
                            s.isEstimatedAdditionalFundsOperand ? i : lastIdx, -1
                        );

                        return filteredSections.map((section, index) => {
                            const isLastOperand = section.isEstimatedAdditionalFundsOperand && index === lastOperandIndex;

                            let paddingClass: string;
                            if (section.label === 'Estimated Additional Funds') {
                                paddingClass = 'pt-4 pb-0';
                            } else if (section.isEstimatedAdditionalFundsOperand) {
                                paddingClass = isLastOperand ? 'pt-2 pb-4' : 'pt-2 pb-0';
                            } else {
                                paddingClass = 'py-4';
                            }

                            return (
                                <section
                                    key={section._id}
                                >
                                    <button
                                        className={`flex items-center w-full px-6 ${paddingClass} transition-colors duration-200 ${section.type === 'expandable' ? 'hover:bg-gray-50 cursor-pointer' : 'cursor-default'
                                            }`}
                                        onClick={() => {
                                            if (section.type === 'expandable') {
                                                handleToggle(section._id);
                                            }
                                        }}
                                        aria-expanded={section.type === 'expandable' ? openSection === section._id : undefined}
                                        aria-controls={section.type === 'expandable' ? `section-${section._id}` : undefined}
                                        disabled={section.type !== 'expandable'}
                                        type="button"
                                    >
                                        <div
                                            className={`w-[70%] text-left text-gray-700 ${section.bold ? 'font-bold' : ''
                                                } ${section.color ? 'text-xl' : ''}`}
                                            style={section.color ? { color: section.color } : undefined}
                                        >
                                            {section.dataType === 'Date' && (
                                                <svg
                                                    className="inline-block w-4 h-4 mr-2"
                                                    fill="currentColor"
                                                    viewBox="0 0 20 20"
                                                    aria-hidden="true"
                                                >
                                                    <path d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" />
                                                </svg>
                                            )}
                                            {section.label}
                                            {section.toolTip && (
                                                <Tooltip message={section.toolTip} position="top" maxWidth={320}>
                                                    <span
                                                        aria-label={`Help for ${section.label}`}
                                                        className="text-black inline-flex items-center justify-center w-5 h-5 rounded-full border border-gray-600 bg-white text-xs font-medium ml-1"
                                                    >
                                                        ?
                                                    </span>
                                                </Tooltip>
                                            )}
                                        </div>
                                        {(!shouldHideValue(section.price) ||
                                            (isSummary &&
                                                (section.label === 'Merit Scholarship Estimate' || section.label === 'Need-Based Grant Estimate') &&
                                                section.price === '$0')) && (
                                                <div
                                                    className={`${section.type === 'expandable' ? 'w-[25%]' : 'w-[30%]'}
                                                text-left ${section.bold ? 'font-bold' : ''} text-gray-900 ${section.color ? 'text-xl' : ''}
                                                ${section.dataType === 'Date' ? '' : 'tabular-nums'}`}
                                                    style={section.color ? { color: section.color } : undefined}
                                                >
                                                    {section.dataType === 'Date' ? section.price : ensureCurrencyFormat(section.price)}
                                                </div>
                                            )}
                                        {section.type === 'expandable' && !shouldHideValue(section.price) && (section.label === 'Cost Of Attendance' || isAfterAcademicScores) && (
                                            <div className="w-[5%] flex justify-center">
                                                <svg
                                                    className={`w-5 h-5 transition-transform duration-300 ${openSection === section._id ? 'transform rotate-180' : ''
                                                        }`}
                                                    fill="currentColor"
                                                    viewBox="0 0 20 20"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    aria-hidden="true"
                                                >
                                                    <path
                                                        fillRule="evenodd"
                                                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                                        clipRule="evenodd"
                                                    />
                                                </svg>
                                            </div>
                                        )}
                                    </button>

                                    {section.label === 'Cost Of Attendance' && (
                                        <div className="border-b border-gray-300"></div>
                                    )}

                                    {section.type === 'expandable' && section.subSections && (
                                        <section
                                            id={`section-${section._id}`}
                                            className={`overflow-hidden transition-all duration-500 ease-in-out ${openSection === section._id
                                                ? 'max-h-96 opacity-100 px-6'
                                                : 'max-h-0 opacity-0 px-6'
                                                }`}
                                            aria-labelledby={`section-${section._id}-label`}
                                        >
                                            {section.subSections
                                                .filter(sub => {
                                                    // On summary page, hide subsections with N/A or $0 values
                                                    if (isSummary) {
                                                        return !shouldHideValue(sub.price);
                                                    }
                                                    return true;
                                                })
                                                .map((sub, subIndex) => (
                                                    <div key={`${section._id}-sub-${subIndex}`} className="mb-4">
                                                        <div className={`flex text-gray-800 pt-2 ${section.label === 'Estimated Additional Funds' ? 'font-normal' : 'font-bold'}`}>
                                                            <span className="w-[70%] text-left pr-2">{sub.label}</span>
                                                            {!shouldHideValue(sub.price) && (
                                                                <span className="w-[30%] text-left tabular-nums">{ensureCurrencyFormat(sub.price)}</span>
                                                            )}
                                                        </div>
                                                        {sub.details && sub.details.length > 0 && (
                                                            <ul className="space-y-1 mt-2">
                                                                {sub.details
                                                                    .filter(detail => {
                                                                        // On summary page, hide details with N/A or $0 values
                                                                        if (isSummary) {
                                                                            return !shouldHideValue(detail.cost);
                                                                        }
                                                                        return true;
                                                                    })
                                                                    .map((detail, detailIndex) => (
                                                                        <li
                                                                            key={`${section._id}-detail-${detailIndex}`}
                                                                            className="flex text-gray-600 text-sm"
                                                                        >
                                                                            <span className="w-[70%] text-left pr-4">{detail.item}</span>
                                                                            {!shouldHideValue(detail.cost) && (
                                                                                <span className="w-[30%] text-left tabular-nums">{ensureCurrencyFormat(detail.cost)}</span>
                                                                            )}
                                                                        </li>
                                                                    ))}
                                                            </ul>
                                                        )}
                                                    </div>
                                                ))}
                                        </section>
                                    )}
                                </section>
                            );
                        });
                    })()}
                </main>
            </div>

            {isSummary && (
                <div className="w-full max-w-xl mt-6 flex justify-between space-x-4">
                    <SummaryActionButton label="Email My Results" color={backgroundColor} onClick={() => setShowEmailModal(true)} />
                    <SummaryActionButton label="Print My Results" color={backgroundColor} onClick={() => setShowPrintPreview(true)} />
                </div>
            )}

            {/* Email results modal */}
            <EmailResultsModal
                isOpen={showEmailModal}
                onClose={() => setShowEmailModal(false)}
                accentColor={textColor ?? undefined}
                backgroundColor={backgroundColor ?? undefined}
                estimateId={estimateId ?? ''}
                disclaimerText={disclaimerText}
                noteText={noteText}
            />

            {/* Print preview modal */}
            <PrintPreview
                isOpen={showPrintPreview}
                onClose={() => setShowPrintPreview(false)}
                derivedAttributesData={derivedAttributesData}
            />
        </div>
    );
};

export default EstimatedPrice;